import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import './Navbar.css';
import { useUser } from '../../auth/AuthPage/UserContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import NotificationBadge from '../NotificationBadge/NotificationBadge';
import AvatarFrame from '../AvatarFrame/AvatarFrame';
import Icon from '../Icon/Icon';
import { useMessages } from '../../../contexts/MessagesContext';

function Navbar() {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
    const avatarRef = useRef(null);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const { user, logout } = useUser();
    const { theme, toggleTheme } = useTheme();
    const { total: notificationCount, byTab = { incoming: 0, pending: 0, completed: 0 } } = useNotifications();
    const { unread: chatUnread, requests: chatRequests } = useMessages();

    // Section navigation is flat links - Trade's four sections merged
    // into one dashboard, and Binders' Collection/Wishlist live as
    // on-page tabs, so there's nothing left to drop down to.
    const tradeBadge = (byTab.incoming || 0) + (byTab.pending || 0) + (byTab.completed || 0);

    // Close desktop dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (
                desktopDropdownOpen &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                avatarRef.current &&
                !avatarRef.current.contains(event.target)
            ) {
                setDesktopDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [desktopDropdownOpen]);

    useEffect(() => {
        function handleResize() {
            if (window.innerWidth >= 800) {
                setIsMobileMenuOpen(false);
            }
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(open => !open);
        if (isMobileMenuOpen) setUserMenuOpen(false); // close user menu when closing nav
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
        setUserMenuOpen(false);
    };

    const goToAuth = () => navigate('/auth');

    // Dark mode lives in the profile menu (the footer keeps a switch
    // only for logged-out visitors). The menu stays open on toggle.
    const themeRow = (cls) => (
        <button type="button" className={`${cls} theme-row`} role="switch"
            aria-checked={theme === 'dark'} onClick={toggleTheme}>
            <span>Dark mode</span>
            <span className={`theme-switch${theme === 'dark' ? ' on' : ''}`} aria-hidden="true"><i /></span>
        </button>
    );

    return (
        <nav className="navbar">
            {/* Logo Section */}
            <div className="navbar-logo">
                <Link to="/" onClick={closeMobileMenu}>
                    <div className="logo-container">
                        <img
                            src="/assets/Logo.png"
                            alt="MTG Trader Logo"
                            className={`logo-image ${theme === 'light' ? 'logo-visible' : 'logo-hidden'}`}
                        />
                        <img
                            src="/assets/LogoWhite.png"
                            alt="MTG Trader Logo"
                            className={`logo-image logo-white ${theme === 'dark' ? 'logo-visible' : 'logo-hidden'}`}
                        />
                    </div>
                </Link>
            </div>

            {/* Hamburger Menu Button (Mobile Only) */}
            <button
                className={`mobile-menu-toggle mobile-only${isMobileMenuOpen ? ' open' : ''}`}
                onClick={toggleMobileMenu}
                aria-label="Toggle navigation menu"
            >
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
            </button>

            {/* Desktop Navigation Links */}
            <div className="navbar-links desktop-only">
                <NavLink to="/" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>Home</NavLink>
                <NavLink to="/search" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>Search</NavLink>
                <NavLink to="/requests" className={({ isActive }) => `nav-link nav-link-badged${isActive ? ' active' : ''}`}>
                    Trade
                    {tradeBadge > 0 && <NotificationBadge count={tradeBadge} size="small" />}
                </NavLink>
                <NavLink to="/binders" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>Binders</NavLink>
            </div>

            {/* Desktop Auth/Profile Button */}
            <div className="navbar-auth desktop-only">
                {!user ? (
                    <button className="btn btn-primary" onClick={goToAuth}>Log In / Sign Up</button>
                ) : (
                    <>
                    {/* Chats: unread count, or a quiet dot when only
                        message requests are waiting */}
                    <NavLink
                        to="/messages"
                        className={({ isActive }) => `nav-chat${isActive ? ' active' : ''}`}
                        aria-label={`Messages${chatUnread ? `, ${chatUnread} unread` : ''}`}
                        title="Messages"
                    >
                        <Icon name="chat" size={22} />
                        {chatUnread > 0
                            ? <NotificationBadge count={chatUnread} size="small" className="nav-chat-badge" />
                            : chatRequests > 0 && <span className="nav-chat-dot" />}
                    </NavLink>
                    <div className="navbar-avatar-wrapper" style={{ marginRight: '2rem', position: 'relative' }} ref={avatarRef}>
                        <div className="navbar-avatar-container" onClick={() => setDesktopDropdownOpen(open => !open)}>
                        <AvatarFrame user={user}>
                            <img
                                key={user?.profile_photo || 'placeholder'}
                                src={getProfilePhotoSrc(user)}
                                alt="Profile"
                                className="navbar-avatar"
                                style={{ cursor: 'pointer' }}
                                onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
                            />
                        </AvatarFrame>
                            {notificationCount > 0 && (
                                <NotificationBadge count={notificationCount} size="small" className="navbar-avatar-badge" />
                            )}
                        </div>
                        {desktopDropdownOpen && (
                            <div className="navbar-dropdown" ref={dropdownRef}>
                                <div className="dropdown-username-row">
                                    <span>Logged in as </span><strong>{user.username}</strong>
                                </div>
                                <button className="dropdown-item" onClick={() => { setDesktopDropdownOpen(false); navigate('/profile'); }}>My Profile</button>
                                <button className="dropdown-item" onClick={() => { setDesktopDropdownOpen(false); navigate('/account'); }}>Account Settings</button>
                                {/* access_role gates pages (a suspended shop keeps its
                                    dashboard); the plain role only drives visuals */}
                                {['shop', 'admin'].includes(user.access_role || user.role) && (
                                    <button className="dropdown-item" onClick={() => { setDesktopDropdownOpen(false); navigate('/shop'); }}>My Shop</button>
                                )}
                                {(user.access_role || user.role) === 'admin' && (
                                    <button className="dropdown-item" onClick={() => { setDesktopDropdownOpen(false); navigate('/admin'); }}>Admin</button>
                                )}
                                {themeRow('dropdown-item')}
                                <button className="dropdown-logout" onClick={async () => { await logout(); setDesktopDropdownOpen(false); navigate('/'); }}>Log Out</button>
                            </div>
                        )}
                    </div>
                    </>
                )}
            </div>

            {/* Mobile Menu Dropdown (Always rendered for animation) */}
            <div className={`mobile-menu${isMobileMenuOpen ? ' open' : ''}`}>
                <div className="mobile-menu-content">
                    {/* User info at the top in mobile view */}
                    {user && (
                        <div className="mobile-user-row" onClick={() => setUserMenuOpen(open => !open)} style={{ cursor: 'pointer' }}>
                            <div className="mobile-avatar-container">
                            <AvatarFrame user={user}>
                                <img
                                    key={user?.profile_photo || 'placeholder'}
                                    src={getProfilePhotoSrc(user)}
                                    alt="Profile"
                                    className="navbar-avatar"
                                    onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
                                />
                            </AvatarFrame>
                                {notificationCount > 0 && (
                                    <NotificationBadge count={notificationCount} size="small" className="mobile-avatar-badge" />
                                )}
                            </div>
                            <span className="navbar-username">{user.username}</span>
                        </div>
                    )}
                    {user && (
                        <div className={`mobile-user-dropdown${userMenuOpen ? ' open' : ''}`}>
                            {userMenuOpen && (
                                <>
                                    <button className="mobile-nav-link" onClick={() => { setUserMenuOpen(false); closeMobileMenu(); navigate('/profile'); }}>My Profile</button>
                                    <button className="mobile-nav-link" onClick={() => { setUserMenuOpen(false); closeMobileMenu(); navigate('/account'); }}>Account Settings</button>
                                    {['shop', 'admin'].includes(user.access_role || user.role) && (
                                        <button className="mobile-nav-link" onClick={() => { setUserMenuOpen(false); closeMobileMenu(); navigate('/shop'); }}>My Shop</button>
                                    )}
                                    {(user.access_role || user.role) === 'admin' && (
                                        <button className="mobile-nav-link" onClick={() => { setUserMenuOpen(false); closeMobileMenu(); navigate('/admin'); }}>Admin</button>
                                    )}
                                    {themeRow('mobile-nav-link')}
                                    <button className="dropdown-logout" onClick={async () => { await logout(); setUserMenuOpen(false); closeMobileMenu(); navigate('/'); }}>Log Out</button>
                                </>
                            )}
                        </div>
                    )}
                    <NavLink to="/" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={closeMobileMenu}>Home</NavLink>
                    <NavLink to="/search" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={closeMobileMenu}>Search</NavLink>
                    <NavLink to="/requests" className={({ isActive }) => `mobile-nav-link mobile-nav-link-with-badge${isActive ? ' active' : ''}`} onClick={closeMobileMenu}>
                        Trade
                        {tradeBadge > 0 && <NotificationBadge count={tradeBadge} size="small" />}
                    </NavLink>
                    <NavLink to="/binders" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={closeMobileMenu}>Binders</NavLink>
                    {user && (
                        <NavLink to="/messages" className={({ isActive }) => `mobile-nav-link mobile-nav-link-with-badge${isActive ? ' active' : ''}`} onClick={closeMobileMenu}>
                            Messages
                            {chatUnread > 0 && <NotificationBadge count={chatUnread} size="small" />}
                        </NavLink>
                    )}
                    <div className="mobile-auth-buttons">
                        {!user && (
                            <button className="btn btn-primary" onClick={goToAuth}>Log In / Sign Up</button>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
}

export default Navbar;
